// src/pages/MedCats.jsx
import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import axios from "axios";
import { toast } from "react-toastify";

import DelBox from '../comps/DelBox';
import AddMedCat from '../models/AddMedCat';
import AppConst from "../apis/appconst";

const MedCats = () => {
  const [formData, setFormData] = useState({
    l1: [],
    cat_type: 'medcats',
    page_no: 0,
    alertMsg: '',
    alertMsgTyp: '',
    search_key: '',
    isAlert: false,
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [ldata, setLdata] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const initData = async () => {
    const storedData = sessionStorage.getItem("ldata");
    if (!storedData) return;
    const parsedUser = JSON.parse(storedData);
    setLdata(parsedUser);
    const roleId = parsedUser?.role_id;
    setIsAdmin(roleId === 1 || roleId === "1");

    setLoading(true);
    const fd = new FormData();
    fd.append('cat_type', formData.cat_type + "");
    fd.append('page_no', formData.page_no + "");
    fd.append('search_key', formData.search_key + "");
    fd.append('par_by', 1 + "");
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data && response.data.status) {
        const records = Array.isArray(response.data.records) ? response.data.records : [];
        setFormData(prev => ({ ...prev, l1: records }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    } catch (err) {
      console.error("initData error", err);
      setFormData(prev => ({ ...prev, l1: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // on mount load
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setLdata(parsed);
      const roleId = parsed?.role_id;
      setIsAdmin(roleId === 1 || roleId === "1");
      initData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // keep search reactive but call initData only on explicit actions or debounced if wanted
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // SweetAlert confirm and then delete
  const confirmAndDelete = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      html: `<strong>Category:</strong> ${String(item.cat_name || item.cat_type1 || "")}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      await delItem(item);
    }
  };

  const delItem = async (editItem) => {
    if (!editItem || !editItem.cat_id) {
      toast.error("Invalid item selected for delete");
      return;
    }
    const fd = new FormData();
    fd.append('cat_id', editItem.cat_id + "");
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.delCatUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data && response.data.status) {
        toast.success("Category deleted successfully");
        // refresh
        initData();
      } else {
        const msg = response.data?.message || "Delete failed";
        toast.error(msg);
      }
    } catch (error) {
      console.error("delItem error", error);
      toast.error("Server error while deleting category");
    }
  };

  const handleAddDepart = () => {
    setFormData(prev => ({ ...prev, page_no: 0 }));
    initData();
  };

  // client-side filtered list (search box)
  const filteredList = useMemo(() => {
    const key = (formData.search_key || "").trim().toLowerCase();
    if (!key) return formData.l1 || [];
    return (formData.l1 || []).filter(item =>
      (`${item.cat_name || ''}`.toLowerCase().includes(key) ||
       `${item.cat_type1 || ''}`.toLowerCase().includes(key))
    );
  }, [formData.l1, formData.search_key]);

  // DataTable columns
  const columns = [
    {
      name: 'SN',
      selector: (row, idx) => idx + 1,
      width: "70px",
      sortable: false,
      center: true,
    },
    {
      name: 'Category',
      selector: row => row.cat_name || '',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Description',
      selector: row => row.cat_type1 || '',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Action',
      cell: row => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
            disabled={!isAdmin}
            style={{ opacity: !isAdmin ? 0.5 : 1, pointerEvents: !isAdmin ? "none" : "auto" }}
            onClick={(e) => {
              e.preventDefault();
              setEditItem(row);
              // open modal trigger
              const bt = document.getElementById("addbt453");
              if (bt) bt.click();
            }}
          >
            <i className="ri-pencil-fill fs-16 me-1"></i> Edit
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center"
            disabled={!isAdmin}
            style={{ opacity: !isAdmin ? 0.5 : 1, pointerEvents: !isAdmin ? "none" : "auto" }}
            onClick={(e) => {
              e.preventDefault();
              confirmAndDelete(row);
            }}
          >
            <i className="ri-delete-bin-5-fill fs-16 me-1"></i> Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "250px",
    }
  ];
  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#f1f5fb',
        color: '#405189',
        fontSize: '15px',      // larger header text
        fontWeight: '700',
        paddingLeft: '14px',
        paddingRight: '14px',
      }
    },
    rows: {
      style: {
        fontSize: '16px', // row font size
        minHeight: '58px',
      },
    },
    cells: {
      style: {
        paddingLeft: '14px',
        paddingRight: '14px',
        fontSize: '15px',      // larger body text
        lineHeight: '20px'
      }
    },
  };
  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="orderList">
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Medicine Categories</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button id="addbt453" style={{ display: "none" }} type="button" className="btn btn-success add-btn" data-bs-toggle="modal" data-bs-target="#showModal" />
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          setEditItem(null);
                          const bt = document.getElementById("addbt453");
                          if (bt) bt.click();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Add Category
                      </button>
                      <button className="btn btn-soft-danger" id="remove-actions">
                        <i className="ri-delete-bin-2-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body border border-dashed border-end-0 border-start-0">
                <form>
                  <div className="row g-3">
                    <div className="col-xxl-5 col-sm-6">
                      <div className="search-box">
                        <input
                          className="form-control search"
                          name="search_key"
                          onChange={handleChange}
                          value={formData.search_key}
                          placeholder="Search by name"
                          type="text"
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="card-body pt-3">
                <div>
                  <div className="table-responsive table-card mb-1">
                    {/* React DataTable */}
                    <DataTable
                      columns={columns}
                      data={filteredList}
                      progressPending={loading}
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[5, 10, 20, 50]}
                      selectableRows={false}
                      customStyles={customStyles}
                      persistTableHead
                      highlightOnHover
                      dense
                      responsive
                      noDataComponent={<div className="p-3 text-center text-muted">No records found</div>}
                    />
                  </div>

                  {/* <div className="d-flex justify-content-end">
                    <div className="pagination-wrap hstack gap-2">
                      <a className="page-item pagination-prev" href="#!" onClick={(e) => { e.preventDefault(); if (formData.page_no > 0) { setFormData(prev => ({ ...prev, page_no: prev.page_no - 1 })); initData(); } }}>Previous</a>
                      <ul className="pagination listjs-pagination mb-0"></ul>
                      <a className="page-item pagination-next" href="#!" onClick={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, page_no: prev.page_no + 1 })); initData(); }}>Next</a>
                    </div>
                  </div> */}
                </div>

                <AddMedCat item={editItem} onAddMedCat={handleAddDepart} />
                <DelBox />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedCats;
